/**
 * Layer 3: ViewerProfile Context
 *
 * Provides the current viewer profile and controls what content is shown
 * based on the target audience.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import type { ViewerProfile, ViewerProfileConfig } from '@/types/viewer-profile'
import { VIEWER_PROFILE_CONFIGS } from '@/types/viewer-profile'

interface ViewerProfileContextValue {
  currentProfile: ViewerProfile
  profileConfig: ViewerProfileConfig
  setProfile: (profile: ViewerProfile) => void
  availableProfiles: ViewerProfile[]
  isSectionVisible: (sectionId: string) => boolean
  isSectionHidden: (sectionId: string) => boolean
}

const ViewerProfileContext = createContext<ViewerProfileContextValue | undefined>(undefined)

interface ViewerProfileProviderProps {
  children: ReactNode
  initialProfile?: ViewerProfile
}

export function ViewerProfileProvider({
  children,
  initialProfile = 'public-view',
}: ViewerProfileProviderProps) {
  const [currentProfile, setCurrentProfile] = useState<ViewerProfile>(initialProfile)

  const setProfile = useCallback((profile: ViewerProfile) => {
    setCurrentProfile(profile)
    // Persist to localStorage (browser only)
    if (typeof window !== 'undefined') {
      localStorage.setItem('viewerProfile', profile)
    }
  }, [])

  const isSectionVisible = useCallback(
    (sectionId: string) => {
      const config = VIEWER_PROFILE_CONFIGS[currentProfile]
      return config.visibleSections.includes(sectionId)
    },
    [currentProfile]
  )

  const isSectionHidden = useCallback(
    (sectionId: string) => {
      const config = VIEWER_PROFILE_CONFIGS[currentProfile]
      return config.hiddenSections.includes(sectionId)
    },
    [currentProfile]
  )

  const value: ViewerProfileContextValue = {
    currentProfile,
    profileConfig: VIEWER_PROFILE_CONFIGS[currentProfile],
    setProfile,
    availableProfiles: Object.keys(VIEWER_PROFILE_CONFIGS) as ViewerProfile[],
    isSectionVisible,
    isSectionHidden,
  }

  return (
    <ViewerProfileContext.Provider value={value}>
      {children}
    </ViewerProfileContext.Provider>
  )
}

export function useViewerProfile() {
  const context = useContext(ViewerProfileContext)
  if (!context) {
    throw new Error('useViewerProfile must be used within ViewerProfileProvider')
  }
  return context
}
